import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  State,
  getExhibitionSettings,
  getLoginTimestamp,
  getSelectedExhibitionId,
  isEventSeriesPage,
  selectInitializeConteoLogin,
  selectIsLogoutProcessing
} from '@app/app.reducer';
import { AppService } from '@app/app.service';
import {
  CreateUserProfileRequest,
  CreateUserProfileResponse,
  UpdateUserProfileRequest,
  UpdateUserProfileResponse
} from '@core/data-access/models/api/user-profile.model';
import { BaseRoute } from '@core/data-access/models/routes.model';
import { OrderPreparationService } from '@core/data-access/services/order-preparation.service';
import { millisecondsToLoginSessionExpiry } from '@core/utils/datetime-helpers';
import { Store, select } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { SetIsLogoutProcessing } from '@store/helpers/helper.actions';
import { HelperService } from '@store/helpers/helper.service';
import { getClaimedTicketHash } from '@store/products/booking/booking.selectors';
import { selectAreProductSelectionProductsLoaded } from '@store/products/product-selection/product-selection.selectors';
import { isEmpty, isNil } from 'lodash';
import { combineLatest, filter, first, iif, map, of, switchMap, timer, withLatestFrom } from 'rxjs';
import { SetLanguage } from './user.actions';
import { BillingAddressModel } from './user.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store<State>);
  private readonly helperService = inject(HelperService);
  private readonly appService = inject(AppService);
  private readonly orderPreparationService = inject(OrderPreparationService);

  loggedInId: number;
  loggedInUserId: any;

  initLoginTimestampExpirationSubscription() {
    return this.store
      .pipe(
        select(getLoginTimestamp),
        filter((loginTimestamp) => !isNil(loginTimestamp)),
        switchMap((loginTimestamp) => timer(millisecondsToLoginSessionExpiry(loginTimestamp))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.logout(true));
  }

  forgottenPassword(data) {
    return this.http.post(`${environment.protocol}${environment.webApiUrl}/user/forgotten-password`, data);
  }

  logout(isLoginSessionExpired: boolean = false) {
    if (this.orderPreparationService.isOrderProcessing()) {
      return;
    }

    this.store.dispatch(new SetIsLogoutProcessing(true));

    const baseRoute = this.helperService.isSelfRegistration()
      ? BaseRoute.SelfRegistration
      : window.location.pathname.includes(BaseRoute.WebShopDownload)
        ? BaseRoute.WebShopDownload
        : BaseRoute.WebShop;

    combineLatest({
      queryParams: this.activatedRoute.queryParams,
      selectedExhibitionId: this.store.select(getSelectedExhibitionId),
      eventSeriesPage: this.store.select(isEventSeriesPage),
      claimedTicketHash: this.store.select(getClaimedTicketHash),
      initializeConteoLogin: this.store.select(selectInitializeConteoLogin)
    })
      .pipe(
        first(),
        switchMap(
          ({
            queryParams: { voucher },
            selectedExhibitionId,
            eventSeriesPage,
            claimedTicketHash,
            initializeConteoLogin
          }) =>
            this.appService.resetReducersWithUser().pipe(
              map(() => ({
                voucher,
                selectedExhibitionId,
                eventSeriesPage,
                claimedTicketHash,
                initializeConteoLogin
              }))
            )
        )
      )
      .subscribe(({ voucher, selectedExhibitionId, eventSeriesPage, claimedTicketHash, initializeConteoLogin }) => {
        if (isLoginSessionExpired) {
          window.location.replace(`/${BaseRoute.Logout}`);
        } else if (selectedExhibitionId) {
          let queryParams = '';

          if (baseRoute.includes(BaseRoute.WebShopDownload)) {
            queryParams = `?t=${claimedTicketHash}`;
          }

          const passVoucherQueryParamToConteoLoginAfterCurrentUserLogout = !isEmpty(voucher) && initializeConteoLogin;

          if (passVoucherQueryParamToConteoLoginAfterCurrentUserLogout) {
            queryParams = `?voucher=${voucher}`;
          }

          window.location.replace(`/${baseRoute}/${selectedExhibitionId}` + queryParams);
        } else if (eventSeriesPage?.isEventSeries) {
          window.location.replace(`/${BaseRoute.Series}/${eventSeriesPage.eventSeriesId}`);
        } else {
          window.location.replace(`/`);
        }
      });
  }

  resetIsLogoutProcessingSubscription() {
    return this.isExhibitionLoadedAfterLogoutReload$().subscribe(() =>
      this.store.dispatch(new SetIsLogoutProcessing(false))
    );
  }

  isExhibitionLoadedAfterLogoutReload$() {
    const isReloaded$ = this.helperService.isReloaded$();
    const isSelectExhibitionPage$ = this.helperService.isSelectExhibitionPage$();
    const isExhibitionLoaded$ = combineLatest({
      exhibitionSettings: this.store.select(getExhibitionSettings),
      areProductSelectionProductsLoaded: this.store.select(selectAreProductSelectionProductsLoaded)
    }).pipe(
      map(
        ({ exhibitionSettings, areProductSelectionProductsLoaded }) =>
          !isNil(exhibitionSettings) && areProductSelectionProductsLoaded
      )
    );

    return combineLatest({
      isReloaded: isReloaded$,
      isSelectExhibitionPage: isSelectExhibitionPage$
    }).pipe(
      withLatestFrom(this.store.select(selectIsLogoutProcessing)),
      filter(([_, isLogoutProcessing]) => isLogoutProcessing),
      switchMap(([{ isSelectExhibitionPage }]) => iif(() => isSelectExhibitionPage, of(true), isExhibitionLoaded$)),
      first((isExhibitionLoadedAfterLogout) => isExhibitionLoadedAfterLogout),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  changePreferredLanguage(lang) {
    this.store.dispatch(new SetLanguage(lang));
  }

  login(username, password) {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/login`,
      JSON.stringify({ username, password }),
      httpOptions
    );
  }

  getRegistrationQuestionnaire(eventId) {
    let selfReg = '';

    if (this.helperService.isSelfRegistration()) {
      selfReg = '?sr=true';
    }

    return this.http.get(
      `${environment.protocol}${environment.webApiUrl}/event/${eventId}/registration-questionnaire${selfReg}`
    );
  }

  saveRegistrationQuestionnaire(questionnaireData) {
    let selfReg = '';

    if (this.helperService.isSelfRegistration()) {
      selfReg = '?sr=true';
    }

    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/questionnaire${selfReg}`,
      questionnaireData
    );
  }

  downloadTicketById(ticketId) {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/user/download-ticket/${ticketId}`, {
      responseType: 'blob'
    });
  }

  downloadInvoice(orderId) {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/invoice-download`, {
      responseType: 'blob'
    });
  }

  downloadPassbook(ticketId) {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/user/entry-tickets/${ticketId}/passbook`, {
      responseType: 'blob'
    });
  }

  downloadRefundReceipt(orderId) {
    return this.http.get(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/refund-receipt-download`,
      { responseType: 'blob' }
    );
  }

  refundOrder(orderId) {
    return this.http.post(`${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/refund`, {});
  }

  refundSingleTicket(orderId: number, ticketId: number) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/entry-tickets/${ticketId}/refund`,
      {}
    );
  }

  downloadTicketRefundReceipt(orderId, ticketId) {
    return this.http.get(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/entry-tickets/${ticketId}/refund-receipt-download`,
      { responseType: 'blob' }
    );
  }

  downloadPackageRefundReceipt(orderId, packageIndex) {
    return this.http.get(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/packages/${packageIndex}/refund-package-receipt-download`,
      { responseType: 'blob' }
    );
  }

  refundPackage(orderId, packageIndex, cancelOrderBody) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/packages/${packageIndex}/refund-package`,
      cancelOrderBody
    );
  }

  refundParkingTicket(orderId: number, ticketId: number) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/parking-tickets/${ticketId}/refund`,
      {}
    );
  }

  downloadParkingRefundReceipt(orderId, ticketId) {
    return this.http.get(
      `${environment.protocol}${environment.webApiUrl}/user/orders/${orderId}/parking-tickets/${ticketId}/refund-receipt-download`,
      { responseType: 'blob' }
    );
  }

  downloadParkingTicketById(parkingTicketId) {
    return this.http.get(
      `${environment.protocol}${environment.webApiUrl}/user/download-parking-ticket/${parkingTicketId}`,
      { responseType: 'blob' }
    );
  }

  getEmailUnsubscriptions() {
    return this.http.get(`${environment.webApiUrl}/unsubscriptions`);
  }

  getProfile() {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/user`);
  }

  createProfile(createUserProfileRequest: CreateUserProfileRequest) {
    const selfReg = this.helperService.isSelfRegistration() ? '?sr=true' : '';

    return this.http.post<CreateUserProfileResponse>(
      `${environment.protocol}${environment.webApiUrl}/user${selfReg}`,
      createUserProfileRequest
    );
  }

  updateProfile(updateUserProfileRequest: UpdateUserProfileRequest) {
    return this.http.patch<UpdateUserProfileResponse>(
      `${environment.protocol}${environment.webApiUrl}/user`,
      updateUserProfileRequest
    );
  }

  updateProfileWithoutValidation(updateUserProfileRequest: UpdateUserProfileRequest) {
    return this.http.patch<UpdateUserProfileResponse>(
      `${environment.protocol}${environment.webApiUrl}/user/novalidate`,
      updateUserProfileRequest
    );
  }

  getProfileBillingAddress() {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/user/billing-address`);
  }

  putProfileBillingAddress(addressId: number, data: BillingAddressModel) {
    return this.http.put(`${environment.protocol}${environment.webApiUrl}/user/billing-address/${addressId}`, data);
  }

  postProfileBillingAddress(data: BillingAddressModel) {
    return this.http.post(`${environment.protocol}${environment.webApiUrl}/user/billing-address`, data);
  }

  deleteProfileBillingAddress(addressId: number) {
    return this.http.delete(`${environment.protocol}${environment.webApiUrl}/user/billing-address/${addressId}`);
  }

  setNewPassword(password, language) {
    return this.http.post(`${environment.protocol}${environment.webApiUrl}/user/password/${language}`, {
      password
    });
  }

  resetPassword(password, hash, crmHash) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/password/reset`,
      {
        password,
        hash,
        crmHash
      },
      { observe: 'response' }
    );
  }

  setNewEmail(email, password, language) {
    return this.http.post(`${environment.protocol}${environment.webApiUrl}/user/email/${language}`, {
      email,
      password
    });
  }

  verifyEmail(hash, crmHash) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/verify-email`,
      {
        hash,
        crmHash
      },
      { observe: 'response' }
    );
  }

  sendVerifyEmail(language) {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/user/verify-email/${language}`, {
      observe: 'response'
    });
  }

  changePassword(hash, crmHash) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/password`,
      {
        hash,
        crmHash
      },
      { observe: 'response' }
    );
  }

  changeEmail(hash, crmHash) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/email`,
      {
        hash,
        crmHash
      },
      { observe: 'response' }
    );
  }

  getListOfSocialLogins(eventId: number) {
    return this.http.get<string[]>(
      `${environment.protocol}${environment.webApiUrl}/user/external-login/available?eventId=${eventId}`
    );
  }

  getListOfSocialShares() {
    return this.http.get<string[]>(
      `${environment.protocol}${environment.webApiUrl}/user/external-login/available-for-share`
    );
  }
}
